import React, { useState, useEffect } from 'react';
import { Card, Typography, Input, Button } from "@material-tailwind/react";

// Basic Google Drive link format check (frontend-friendly)
const isProbablyGoogleDrive = (url = '') => {
    if (!url) return false;
    const pattern = /(?:https?:\/\/)?(?:drive\.google\.com|docs\.google\.com)\/(?:file\/d\/|open\?id=)[A-Za-z0-9_-]+/i;
    return pattern.test(url);
};

const createEmptyPayload = (username) => ({
    driver_id: null,
    driver_name: '',
    license_no: '',
    license_expiry: '',
    sticker_link: '',
    notes: '',
    verification_status: 'VERIFIED',
    created_by: username || 'system',
    created_at: new Date().toISOString()
});

const DriverLicenseVerification = ({ apiCall, user, onSuccess, initial = {}, onClose }) => {
    const [form, setForm] = useState(() => ({ ...createEmptyPayload(user?.username), ...initial }));
    const [errors, setErrors] = useState({});
    const [message, setMessage] = useState({ type: '', text: '' });
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (user?.username) {
            setForm(prev => ({ ...prev, created_by: user.username }));
        }
    }, [user?.username]);

    useEffect(() => {
        if (initial && Object.keys(initial).length > 0) {
            setForm(prev => ({ ...prev, ...initial }));
        }
    }, [initial]);

    const validate = (values) => {
        const e = {};
        if (!values.driver_id) e.driver_id = 'Driver is required';
        if (!values.sticker_link || !values.sticker_link.trim()) e.sticker_link = 'Sticker link is required';
        else if (!isProbablyGoogleDrive(values.sticker_link)) e.sticker_link = 'Sticker link must be a Google Drive file link';

        if (values.license_expiry) {
            const d = new Date(values.license_expiry);
            if (isNaN(d.getTime())) e.license_expiry = 'Invalid date';
        }

        return e;
    };

    const handleChange = (key, value) => {
        setForm(prev => ({ ...prev, [key]: value }));
        if (errors[key]) setErrors(prev => {
            const copy = { ...prev };
            delete copy[key];
            return copy;
        });
    };

    const handleSubmit = async () => {
        const validation = validate(form);
        if (Object.keys(validation).length > 0) {
            setErrors(validation);
            return;
        }

        setSubmitting(true);
        setMessage({ type: '', text: '' });

        const success = form.verification_status === 'VERIFIED' ? true : (form.verification_status === 'FAILED' ? false : null);

        const payload = {
            driver_id: form.driver_id,
            license_no: form.license_no || null,
            license_expiry: form.license_expiry || null,
            sticker_link: form.sticker_link,
            verification_status: form.verification_status,
            success,
            notes: form.notes || null,
            created_by: form.created_by
        };

        try {
            if (apiCall) {
                const res = await apiCall('POST', '/driver-license', payload);
                const data = res.data || {};
                if (data.success === false) {
                    setMessage({ type: 'error', text: data.message || 'Verification failed' });
                } else {
                    setMessage({ type: 'success', text: 'Driver license verification recorded' });
                    setForm(createEmptyPayload(user?.username));
                    if (onSuccess) onSuccess(data);
                }
            } else {
                console.log('Driver license verification payload:', payload);
                setMessage({ type: 'success', text: ' (Local) Driver license verification prepared (no API configured).' });
                setForm(createEmptyPayload(user?.username));
                if (onSuccess) onSuccess(payload);
            }
        } catch (err) {
            console.error('Error submitting driver license verification', err);
            const serverMsg = err?.response?.data?.message || err?.message || 'Submission error';
            setMessage({ type: 'error', text: serverMsg });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div>
            <Card className="bg-white rounded-2xl shadow-xl p-6 border border-gray-100">
                <Typography variant="h5" className="font-bold text-center text-brand-secondary mb-4">
                    Driver License Verification
                </Typography>

                {form.driver_name && (
                    <div className="mb-3 text-sm text-gray-700">Driver: <strong>{form.driver_name}</strong></div>
                )}

                {message.text && (
                    <div className={`mb-4 p-3 rounded-md ${message.type === 'success' ? 'bg-green-50 text-green-700 border border-green-100' : 'bg-red-50 text-red-700 border border-red-100'}`}>
                        {message.text}
                    </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Verification Status</label>
                        <select
                            value={form.verification_status}
                            onChange={(e) => handleChange('verification_status', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-primary"
                        >
                            <option value="VERIFIED">Verified</option>
                            <option value="FAILED">Failed</option>
                            <option value="PENDING">Pending</option>
                        </select>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">License Number</label>
                        <Input
                            type="text"
                            value={form.license_no}
                            readOnly
                            onChange={(e) => handleChange('license_no', e.target.value)}
                            error={errors.license_no}
                            className="bg-gray-50 cursor-not-allowed"
                        />
                        {errors.license_no && <Typography variant="small" color="red">{errors.license_no}</Typography>}
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">License Expiry</label>
                        <Input
                            type="date"
                            value={form.license_expiry}
                            onChange={(e) => handleChange('license_expiry', e.target.value)}
                            error={errors.license_expiry}
                        />
                        {errors.license_expiry && <Typography variant="small" color="red">{errors.license_expiry}</Typography>}
                    </div>

                    <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-2">Sticker Link (Google Drive) <span className="text-red-600">*</span></label>
                        <Input
                            type="text"
                            value={form.sticker_link}
                            onChange={(e) => handleChange('sticker_link', e.target.value)}
                            error={errors.sticker_link}
                        />
                        {errors.sticker_link && <Typography variant="small" color="red">{errors.sticker_link}</Typography>}
                        <Typography variant="small" className="text-gray-500 mt-1">A Google Drive file link to the verification sticker; required.</Typography>
                    </div>

                    <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-2">Notes</label>
                        <textarea
                            value={form.notes}
                            onChange={(e) => handleChange('notes', e.target.value)}
                            className="w-full h-28 p-2 border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-primary"
                        />
                    </div>
                </div>

                <div className="flex justify-end gap-4 mt-6">
                    <Button
                        variant="outlined"
                        onClick={() => {
                            if (onClose) onClose();
                            else setForm(createEmptyPayload(user?.username));
                        }}
                        className="border-gray-400 text-gray-600 hover:bg-gray-50"
                    >
                        Close
                    </Button>

                    <Button
                        onClick={handleSubmit}
                        className="bg-brand-primary hover:bg-brand-primary/90 text-white"
                        disabled={submitting}
                    >
                        {submitting ? 'Submitting...' : 'Record Verification'}
                    </Button>
                </div>
            </Card>
        </div>
    );
};

export default DriverLicenseVerification;
